import { EMPLOYEE_PERMISSIONS } from '~/models/EmployeeRoles/Permissions.enum'

export enum PageName {
  BACKORDERS = 'nazendingen',
  CHECK_DELIVERY = 'controle-levering',
  CUSTOMER_PROFILE = 'klantprofiel',
  DELIVERY_OVERVIEW = 'leveringen-over',
  DISPENSE_GUIDANCE = 'dispense-guidance',
  INDEX = 'index',
  LOAD_FILES = 'bestanden-inladen',
  LOGIN = 'inloggen',
  ORDERS = 'bestellingen',
  PARALLEL_ORDER = 'parallel-bestellen',
  PHARMACIST_INSPECTION = 'apothekers-controle',
  PRESCRIPTION_PROCESSING = 'receptverwerking',
  PRESCRIPTION_PROCESSING_AB = 'receptverwerking-paginated',
  PRINT = 'printen',
  ACCOUNTS = 'accounts',
  GDS_PLANNING = 'gds-planning',
  INVOICES = 'facturen',
  MUTATION_REQUESTS = 'wijzigingen',
  PLANNED_DELIVERIES = 'geplande-leveringen',
  PLANNING_ADJUSTMENTS = 'planning-aanpassingen',
  VIEW_PDF = 'pdf/[prescription_id]',
  INTAKES = 'intake-gesprek',
  PARTNER_SUBSCRIPTION = 'partner-inschrijving',
  CONTROL_PROFILE = 'controle-profiel',
}

const DEFAULT_CAPABILITIES = [
  EMPLOYEE_PERMISSIONS.AppUsers_Read,
  EMPLOYEE_PERMISSIONS.ExternalUserData_Read,
]

export const CAPABILITY = {
  PharmacistCheck_View: [
    ...DEFAULT_CAPABILITIES,
    EMPLOYEE_PERMISSIONS.PharmacistCheck_Read,
  ],
  PharmacistCheck_Judge: [
    ...DEFAULT_CAPABILITIES,
    EMPLOYEE_PERMISSIONS.PharmacistCheck_Update,
  ],
  PrescriptionProcessing_View: [
    ...DEFAULT_CAPABILITIES,
    EMPLOYEE_PERMISSIONS.PrescriptionLine_Read,
    EMPLOYEE_PERMISSIONS.Tasks_Read,
    EMPLOYEE_PERMISSIONS.MedicationAgreement_Read,
  ],
  PrescriptionProcessing_Judge: [
    ...DEFAULT_CAPABILITIES,
    EMPLOYEE_PERMISSIONS.DomainEvents_Prescriptions_Create,
    EMPLOYEE_PERMISSIONS.DomainEvents_Prescriptions_Update,
  ],
  Accounts_Edit: [
    ...DEFAULT_CAPABILITIES,
    EMPLOYEE_PERMISSIONS.DomainEvents_EmployeeUsers_Update,
  ],
  GdsPlanning_View: [
    ...DEFAULT_CAPABILITIES,
    EMPLOYEE_PERMISSIONS.GdsPlanning_Read,
  ],
  Parcel_View: [...DEFAULT_CAPABILITIES, EMPLOYEE_PERMISSIONS.Parcels_Read],
  Parcel_Edit: [...DEFAULT_CAPABILITIES, EMPLOYEE_PERMISSIONS.Parcels_Update],
  MutationRequest_View: [
    ...DEFAULT_CAPABILITIES,
    EMPLOYEE_PERMISSIONS.MedicationAgreement_Read,
    EMPLOYEE_PERMISSIONS.MutationRequest_Read,
  ],
  MutationRequest_Edit: [
    ...DEFAULT_CAPABILITIES,
    EMPLOYEE_PERMISSIONS.DomainEvents_MutationRequests_Create,
    EMPLOYEE_PERMISSIONS.DomainEvents_MutationRequests_Update,
    EMPLOYEE_PERMISSIONS.DomainEvents_MutationRequests_Delete,
  ],
  DeliveryPlanning_View: [...DEFAULT_CAPABILITIES],
  Intakes_View: [...DEFAULT_CAPABILITIES],
  PartnerSubscriptions_View: [...DEFAULT_CAPABILITIES],
  Orders_View: [
    ...DEFAULT_CAPABILITIES,
    EMPLOYEE_PERMISSIONS.OrderRequests_Read,
  ],
  Orders_Edit: [
    EMPLOYEE_PERMISSIONS.DomainEvents_OrderRequests_Create,
    EMPLOYEE_PERMISSIONS.DomainEvents_OrderRequests_Update,
  ],
  LoadFiles_View: [
    EMPLOYEE_PERMISSIONS.GoogleCloudFiles_Read,
    EMPLOYEE_PERMISSIONS.GoogleCloudFiles_Download,
    EMPLOYEE_PERMISSIONS.GoogleCloudFiles_Upload,
  ],
  PlannedDeliveries_View: [
    ...DEFAULT_CAPABILITIES,
    EMPLOYEE_PERMISSIONS.PlannedDeliveries_Read,
    EMPLOYEE_PERMISSIONS.DeliveryItems_Read,
    EMPLOYEE_PERMISSIONS.Stock_Read,
    EMPLOYEE_PERMISSIONS.UsageDetails_Read,
  ],
  PlanningAdjustments_View: [
    ...DEFAULT_CAPABILITIES,
    EMPLOYEE_PERMISSIONS.PlanningAdjustments_Read,
    EMPLOYEE_PERMISSIONS.MedicationAgreement_Read,
  ],
  PlanningAdjustments_Edit: [
    ...DEFAULT_CAPABILITIES,
    EMPLOYEE_PERMISSIONS.DomainEvents_PlanningAdjustments_Create,
    EMPLOYEE_PERMISSIONS.DomainEvents_PlanningAdjustments_Update,
    EMPLOYEE_PERMISSIONS.DomainEvents_PlanningAdjustments_Delete,
  ],
  Invoices_View: [...DEFAULT_CAPABILITIES, EMPLOYEE_PERMISSIONS.Invoices_Read],
  DispenseGuidance_View: [...DEFAULT_CAPABILITIES, EMPLOYEE_PERMISSIONS.DispenseGuidrance_Read],
} as const

export type Capability = (typeof CAPABILITY)[keyof typeof CAPABILITY]

export const CAPABILITY_CONFIG = new Map<PageName, Capability>()
  // Tasks Section
  .set(PageName.CHECK_DELIVERY, CAPABILITY.Parcel_View)
  .set(PageName.PRINT, CAPABILITY.Parcel_View)
  .set(PageName.ORDERS, CAPABILITY.Orders_View)
  .set(PageName.LOAD_FILES, CAPABILITY.LoadFiles_View)
  .set(PageName.BACKORDERS, CAPABILITY.Parcel_View)
  .set(PageName.PARALLEL_ORDER, CAPABILITY.LoadFiles_View)
  .set(PageName.PHARMACIST_INSPECTION, CAPABILITY.PharmacistCheck_View)
  .set(PageName.PRESCRIPTION_PROCESSING, CAPABILITY.PrescriptionProcessing_View)
  .set(PageName.MUTATION_REQUESTS, CAPABILITY.MutationRequest_View)
  .set(PageName.PLANNED_DELIVERIES, CAPABILITY.PlannedDeliveries_View)
  .set(PageName.PLANNING_ADJUSTMENTS, CAPABILITY.PlanningAdjustments_View)
  .set(PageName.INTAKES, CAPABILITY.Intakes_View)
  .set(PageName.PARTNER_SUBSCRIPTION, CAPABILITY.PartnerSubscriptions_View)

  // Pages Section
  .set(PageName.CUSTOMER_PROFILE, DEFAULT_CAPABILITIES)
  .set(PageName.INDEX, CAPABILITY.Parcel_View)
  .set(PageName.DELIVERY_OVERVIEW, CAPABILITY.Parcel_View)
  .set(PageName.GDS_PLANNING, CAPABILITY.GdsPlanning_View)
  .set(PageName.INVOICES, CAPABILITY.GdsPlanning_View)
  .set(PageName.CONTROL_PROFILE, DEFAULT_CAPABILITIES)
  .set(PageName.DISPENSE_GUIDANCE, CAPABILITY.DispenseGuidance_View)

  // Settings Section
  .set(PageName.ACCOUNTS, CAPABILITY.Accounts_Edit)
